import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {ChangeDetectorRef, Component, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {UserService} from '@services/administration/user/user.service';
import {NotificationService} from '@services/notification/notification.service';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {DateService} from '@services/date.service';
import {ArrayOperationService} from '@services/administration/array-operation/array-operation.service';
import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';
import {UserEditComponent} from '@components/administration/user/modal/user-edit.component';
import {User} from '@models/user/user';
import {Organization} from '@models/organization/organization';
import {Role} from '@models/user/role';
import {USER_ROLE} from '@constants/user-role.constant';
import {UtilsService} from '@services/utils/utils.service';
import {ELASTIC_SEARCH_SORT_ORDER} from '@models/elastic-search/request/elastic-search-sort-order.enum';
import {ElasticSearchSortValue} from '@models/elastic-search/request/elastic-search-sort-value';
import {combineLatest, Observable, of, Subject} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {catchError, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {MachineGroup} from '@models/adminstration/machine-group/machine-group';
import {
  UsersBanModalComponent
} from '@components/administration/user/modal/users-ban-modal/users-ban-modal.component';
import {ModalService} from '@services/modal/modal.service';
import {ITEMS_PER_PAGE_OPTIONS} from '@constants/pagination/pagination.constant';
import {PaginateControlSettings} from '@models/paginate-control-settings';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';

@Component({
    selector: 'scitis-admin-user',
    templateUrl: './user.component.html',
    styleUrls: ['./user.component.scss'],
    standalone: false
})
export class UserComponent implements OnInit, OnDestroy {
  public isAdminRole: boolean = false;
  public isDistributorRole: boolean = false;
  public isTechAdminRole: boolean = false;
  public isSuperAdminRole: boolean = false;
  public isTechOrSuperAdmin: boolean = false;
  public isAdminOrHigher: boolean = false;
  public restrictUserInteraction: boolean = false;
  public loggedUserId: string = '';
  public usersList: User[] = new Array<User>();
  public searchTerm: string = '';
  public userCount: number = 0;
  public itemsPerPageOptions: number[] = ITEMS_PER_PAGE_OPTIONS;
  public selectedOrganizationId: number | null = null;
  public selectedMachineryGroupId: number | null = null;
  public machineryGroupsList: MachineGroup[] = [];
  public allOrganizations: Organization[] = new Array<Organization>();
  public badgeClassList: { [key in USER_ROLE]: string } = this.userService.getBadgeClassList();
  public loggedUser: User = new User();
  public isLoading: boolean = false;
  public paginateSettings: PaginateControlSettings = new PaginateControlSettings({
    id: 'userPaginationId',
  });
  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();

  private rootOrganizationId: number = -1;
  private rootMachineryGroupId: number = -1;
  private CURRENT_PAGE_NAME = 'User';
  private sortValue: ElasticSearchSortValue = new ElasticSearchSortValue('username', ELASTIC_SEARCH_SORT_ORDER.ASC);
  private searchSubject$: Subject<string> = new Subject<string>();
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private whiteListedMachineryGroups: null | number[] = null;

  constructor(
      private loadingIndicator: LoadingIndicatorService,
      private firebaseAuthenticationService: FirebaseAuthenticationService,
      private translation: TranslateService,
      private userService: UserService,
      private notificationService: NotificationService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private modalService: ModalService,
      private sortingService: SortingService,
      private dateService: DateService,
      private arrayOperationService: ArrayOperationService,
      private utilsService: UtilsService,
      private router: Router,
      private elasticSearchService: ElasticSearchService,
      private activatedRoute: ActivatedRoute,
      private cd: ChangeDetectorRef
  ) {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(({search}) => {
      this.searchTerm = search || '';
      this.searchSubject$.next(this.searchTerm);
    });
  }

  public get appliedNumberOfFilters(): number {
    const filterArray: boolean[] = [];
    filterArray.push(this.selectedMachineryGroupId != null && this.selectedMachineryGroupId > 0);
    filterArray.push(this.selectedOrganizationId != null && this.selectedOrganizationId > 0);
    return filterArray.filter(v => v).length;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.loadingIndicator.show(this.translation.instant('Loading'));
    this.setUserDetails();
    this.setRestrictedUserInteraction();
    this.setUsers();

    this.searchSubject$
    .pipe(takeUntil(this.destroy$))
    .subscribe(value => {
      this.searchTerm = value;
      this.paginateSettings = {
        ...this.paginateSettings,
        currentPage: 1,
      };
      this.setUsers();
    });
  }

  public onSearchForUsers(event): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {search: event.target.value},
      queryParamsHandling: 'merge',
    });
  }

  public onAddUser(): void {
    const modalRef = this.modalService.open(UserEditComponent, {size: 'lg'});
    modalRef.componentInstance.user = new User();
    modalRef.componentInstance.loggedUser = new User(this.loggedUser);
    modalRef.result.then((result) => {
      this.userCount++;
      this.paginateSettings = {
        ...this.paginateSettings,
        totalItems: this.userCount,
      };
      console.log(`Closed with: ${result as User}`);
      const newUser = result as User;
      this.usersList.push(newUser);
    });
  }

  public onColumnSort({column, direction}: SortEvent, isNotKeyword: boolean = false): void {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    const tempSortValue = this.tableHeaderSortableService.getSortValue({column, direction});
    tempSortValue.notKeyword = isNotKeyword;
    if (tempSortValue.order === ELASTIC_SEARCH_SORT_ORDER.NONE) {
      tempSortValue.field = 'username';
      tempSortValue.order = ELASTIC_SEARCH_SORT_ORDER.ASC;
    }
    this.sortValue = tempSortValue;
    this.setUsers();
  }

  public onItemsPerPageChange(itemsPerPage: number): void {
    this.paginateSettings = {
      ...this.paginateSettings,
      itemsPerPage: itemsPerPage,
      currentPage: 1
    };
    this.setUsers();
  }

  public onLoadPageNumber(pageNumber: number): void {
    this.paginateSettings = {
      ...this.paginateSettings,
      currentPage: pageNumber,
    };
    this.setUsers();
  }

  public onDeleteUserSuccess(user: User): void {
    this.userCount--;
    this.paginateSettings = {
      ...this.paginateSettings,
      totalItems: this.userCount,
    };
    this.arrayOperationService.delete(user, this.usersList, []);
  }

  public onUserUpdated(user: User): void {
    this.arrayOperationService.update(user, this.usersList, [], 'firebaseUid');
    if (user.firebaseUid === this.loggedUserId) {
      this.firebaseAuthenticationService.clear();
      this.setUserDetails();
    }
  }

  public onChangeOrganization(organization: Organization) {
    if (organization) {
      this.selectedOrganizationId = organization.id;
    } else {
      this.selectedOrganizationId = null;
    }

  }

  public onChangeMachineryGroup(machineryGroup: MachineGroup) {
    if (machineryGroup) {
      this.selectedMachineryGroupId = machineryGroup.id;
    } else {
      this.selectedMachineryGroupId = null;
    }
  }

  public setUserDetails(): void {
    this.loadingIndicator.show();
    this.firebaseAuthenticationService.getLoggedUserDetails()
    .pipe(takeUntil(this.destroy$), switchMap((user: User) => {
      this.setAuthenticatedUserRoleAndOrganization(user);
      return combineLatest([
        this.setAllOrganizationsForUser(),
        this.setAllMachineryGroupsForUser()
      ]);
    }))
    .subscribe({
      next: ([allOrganizationsRes, allMachineryGroupRes]) => {
        if (allOrganizationsRes?.result?.data && allOrganizationsRes.result.data.length > 0 && !this.allOrganizations.length) {
          this.allOrganizations = allOrganizationsRes.result.data.sort((a, b) => this.sortingService.sortByName(a, b));
        }
        if (allMachineryGroupRes?.result?.data && allMachineryGroupRes.result.data.length > 0 && !this.machineryGroupsList.length) {
          this.machineryGroupsList = allMachineryGroupRes.result.data;
          if (!this.isSuperAdminRole) {
            const userMachineryGroup = new MachineGroup();
            userMachineryGroup.id = this.loggedUser.machineGroup;
            userMachineryGroup.name = this.loggedUser.machineGroupName;
            this.machineryGroupsList.push(userMachineryGroup);
          }
        }
        this.loadingIndicator.hide();
      },
      error: (error) => {
        this.loadingIndicator.hide();
        console.error(`${this.CURRENT_PAGE_NAME}. Error getting current user details.`, JSON.stringify(error));
      }
    });
  }

  public onBanUsers() {
    if (!this.isTechOrSuperAdmin || this.restrictUserInteraction) {
      return;
    }
    const modalRef = this.modalService.open(UsersBanModalComponent, {size: 'md'});
    modalRef.componentInstance.organizationsList = this.allOrganizations;
    modalRef.result.then((_result) => {
      console.log(`Closed with: ${_result}`);
      this.setUsers();
    });
  }

  public onExportUsersInfo() {
    this.loadingIndicator.show();
    this.firebaseAuthenticationService.getLoggedUserDetails().pipe(
      takeUntil(this.destroy$),
      switchMap(user => this.elasticSearchService.listAllAccessibleUsers(user)),
    ).subscribe({
      next: (users => {
        users = users.map(user => new User(user));
        this.exportUsersDetails(users);
        this.loadingIndicator.hide();
      }),
      error: error => {
        console.error(error);
        this.loadingIndicator.hide();
      }
    });
  }

  public onApplyFilterClick() {
    if ((!this.selectedOrganizationId || !this.selectedMachineryGroupId) && !this.isSuperAdminRole) {
      this.selectedOrganizationId = this.selectedOrganizationId || this.loggedUser.parentId;
      this.selectedMachineryGroupId = this.selectedMachineryGroupId || this.loggedUser.machineGroup;
    }
    this.setUsers();
  }

  private exportUsersDetails(userList: User[]): void {
    const csvHeader = ['No.', 'username', 'displayName', 'role', 'agreementAccepted', 'banned', 'language',
     'machineGroupName', 'id', 'parentId', 'machineGroupId', 'whiteListedMachineryGroupsMap'
    ];
    const data = [csvHeader].concat(
      userList
      .map((user: User, index) => [
        String(index + 1),
        user.username,
        user.displayName,
        user.role,
        String(user.agreementAccepted),
        String(user.banned),
        user.language,
        user.machineGroupName,
        user.firebaseUid,
        String(user.parentId),
        String(user.machineGroup),
        String(user.getWhiteListedMachineryGroups(true)?.join(';'))
      ])
    );
    this.downloadCsv(data);
  }

  private downloadCsv(data: string[][]): void {
    const csv = data.reduce((acc, row) => acc + row.join(',') + '\n', '');

    const todayAsYearMonthDay = this.dateService.getYearMonthDayUsingUTC(new Date);

    const hiddenElement = document.createElement('a');
    hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);
    hiddenElement.target = '_blank';
    hiddenElement.download = 'ExportUserInfo_' + todayAsYearMonthDay + '.csv';
    hiddenElement.click();
  }

  private setAuthenticatedUserRoleAndOrganization(user: User): void {
    if (user.parentId) {
      this.loggedUserId = user.firebaseUid;
      this.loggedUser = user;

      const userRole = new Role(user);
      this.selectedOrganizationId = userRole.isSuperAdminRole ? null : user.parentId;
      this.rootOrganizationId = userRole.isSuperAdminRole ? -1 : user.parentId;
      this.selectedMachineryGroupId = userRole.isSuperAdminRole ? null : user.machineGroup;
      this.rootMachineryGroupId = userRole.isSuperAdminRole ? -1 : user.machineGroup;
      this.whiteListedMachineryGroups = userRole.isSuperAdminRole ? null : user.getWhiteListedMachineryGroups(true);
      this.isSuperAdminRole = userRole.isSuperAdminRole;
      this.isTechAdminRole = userRole.isTechAdminRole;
      this.isAdminRole = userRole.isAdminRole;
      this.isDistributorRole = userRole.isDistributorRole;
      this.isTechOrSuperAdmin = this.isSuperAdminRole || this.isTechAdminRole;
      this.isAdminOrHigher = this.utilsService.isAdminOrHigher(this.isAdminRole, this.isTechAdminRole, this.isSuperAdminRole);
    }
  }

  private setRestrictedUserInteraction(): void {
    this.userService.restrictedUserInteraction()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (success) => {
        this.restrictUserInteraction = success && success.length > 0 && success[0];
      },error: (error) => {
        this.restrictUserInteraction = false;
        const message = this.translation.instant('error determining if there is a restriction on user interaction.');
        this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, false);
      }
    });
  }

  private setAllOrganizationsForUser(): Observable<any> {
    return this.elasticSearchService.listAllOrganizations(this.rootOrganizationId)
    .pipe(catchError(err => {
      const message = this.translation.instant('Error getting organizations list.');
      this.notificationService.error(message, err, this.CURRENT_PAGE_NAME, true);
      return of(err);
    }), startWith(null));
  }

  private setUsers() {
    if (this.elasticSearchService.isInvalidSearchTerm(this.searchTerm)) {
      this.usersList = [];
      this.userCount = 0;
      this.paginateSettings = {...this.paginateSettings, totalItems: this.userCount};
      return;
    }

    this.usersList = new Array<User>();
    this.isLoading = true;
    this.loadingIndicator.show(this.translation.instant('Loading'));
    this.elasticSearchService.listUsers(
      this.searchTerm,
      this.sortValue,
      this.paginateSettings.currentPage,
      this.paginateSettings.itemsPerPage,
      this.selectedOrganizationId,
      this.selectedMachineryGroupId,
      this.whiteListedMachineryGroups,
      this.loggedUserId
    ).pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (success: ElasticSearchResponseEntity) => {
        if (success?.result?.data) {
          this.usersList = success.result.data.map(user => new User(user));
          if (this.searchTerm) {
            this.userCount = success.result.totalHits || 0;
            this.paginateSettings = {...this.paginateSettings, totalItems: this.userCount};
          }
        }
        this.loadingIndicator.hide();
        this.isLoading = false;
        this.cd.detectChanges();
      },
      error: (error) => {
        console.log(error);
        const message = this.translation.instant('Error getting users list.');
        this.loadingIndicator.hide();
        this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
        this.isLoading = false;
        this.cd.detectChanges();
      }
    });

    if (!this.searchTerm) {
      this.setUsersCountForUser().pipe(takeUntil(this.destroy$)).subscribe(usersCountRes => {
        if (usersCountRes) {
          this.userCount = usersCountRes?.result?.totalHits || 0;
          this.paginateSettings = {...this.paginateSettings, totalItems: this.userCount};
        }
      });
    }
  }

  private setAllMachineryGroupsForUser(): Observable<any> {
    return this.elasticSearchService.listAllMachineryGroups(this.rootMachineryGroupId, this.whiteListedMachineryGroups)
    .pipe(catchError(err => {
      console.error(`${this.CURRENT_PAGE_NAME}. Error getting machinery groups list.`, JSON.stringify(err));
      return of(err);
    }), startWith(null));
  }

  private setUsersCountForUser(): Observable<any> {
    return this.elasticSearchService.countUsers(this.rootOrganizationId, this.rootMachineryGroupId, this.whiteListedMachineryGroups, this.loggedUserId)
      .pipe(catchError(err => {
        console.error(`${this.CURRENT_PAGE_NAME}. Error getting users count.`, JSON.stringify(err));
        this.loadingIndicator.hide();
        return of(err);
      }), startWith(null));
  }
}
